// app/components/SpecialPrograms.tsx
'use client';
import { useState } from 'react';
import { Calendar } from 'lucide-react';

interface SpecialProgram {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  gradient: string;
  targetAmount: number;
  collectedAmount: number;
  participantsCount: number;
  endDate: string;
  featured: boolean;
}

const SpecialPrograms = () => {
  const [currentProgram, setCurrentProgram] = useState(0);

  const programs: SpecialProgram[] = [
    {
      id: '1',
      title: 'MERDEKAKAN ANAK BANGSA',
      subtitle: 'DENGAN PENDIDIKAN YANG LAYAK',
      description: 'Program spesial kemerdekaan untuk memberikan akses pendidikan berkualitas bagi anak-anak kurang mampu di seluruh Indonesia',
      image: '👨‍🎓👩‍🎓',
      gradient: 'from-red-500 via-red-600 to-red-700',
      targetAmount: 500000000,
      collectedAmount: 287500000,
      participantsCount: 12500,
      endDate: '31 Agustus 2025',
      featured: true
    },
    {
      id: '2',
      title: 'RUMAH YATIM INDONESIA',
      subtitle: 'MEMBANGUN MASA DEPAN BERSAMA',
      description: 'Pembangunan rumah yatim modern dengan fasilitas lengkap untuk memberikan tempat tinggal yang layak bagi anak-anak yatim',
      image: '🏠❤️',
      gradient: 'from-emerald-500 via-emerald-600 to-emerald-700',
      targetAmount: 750000000,
      collectedAmount: 425000000,
      participantsCount: 8900,
      endDate: '30 September 2025',
      featured: true
    },
    {
      id: '3',
      title: 'KESEHATAN UNTUK SEMUA',
      subtitle: 'AKSES PENGOBATAN GRATIS',
      description: 'Program layanan kesehatan gratis untuk masyarakat kurang mampu dengan fasilitas medis terdepan',
      image: '🏥💚',
      gradient: 'from-blue-500 via-blue-600 to-blue-700',
      targetAmount: 300000000,
      collectedAmount: 189000000,
      participantsCount: 15600,
      endDate: '15 September 2025',
      featured: true
    }
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculateProgress = (collected: number, target: number) => {
    return Math.min((collected / target) * 100, 100);
  };

  const currentProgramData = programs[currentProgram];
  const progress = calculateProgress(currentProgramData.collectedAmount, currentProgramData.targetAmount);

  return (
    <div className="space-y-4 px-6">
      {/* Section Header */}
      <div className="px-2">
        <h3 className="font-bold text-lg text-gray-900 mb-1">
          Program Spesial Agustus
        </h3>
        <p className="text-gray-600 text-sm">
          Program unggulan dengan dampak besar
        </p>
      </div>

      {/* Main Banner */}
      <div className="relative overflow-hidden bg-white/70 backdrop-blur-sm rounded-3xl shadow-lg border border-gray-100/50">
        <div className={`bg-gradient-to-br ${currentProgramData.gradient} relative overflow-hidden`}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-4 right-4 w-32 h-32 border-2 border-white/30 rounded-full"></div>
            <div className="absolute bottom-8 left-8 w-24 h-24 border-2 border-white/20 rounded-full"></div>
            <div className="absolute top-1/2 right-1/3 w-16 h-16 border border-white/25 rounded-full"></div>
            <div className="absolute bottom-4 right-4 w-20 h-20 border border-white/15 rounded-full"></div>
          </div>

          <div className="relative z-10 p-6 pb-5">
            <div className="flex items-start justify-between">
              {/* Content */}
              <div className="flex-1 text-white">
                {/* Badge */}
                <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-white/30">
                  <Calendar className="w-4 h-4" />
                  <span className="text-[10px] font-semibold">Berakhir {currentProgramData.endDate}</span>
                </div>

                {/* Title */}
                <h2 className="text-xl font-bold leading-tight mb-1">
                  {currentProgramData.title}
                </h2>
                <h3 className="text-xl font-bold leading-tight mb-1 opacity-90">
                  {currentProgramData.subtitle}
                </h3>

                {/* Description */}
                <p className="text-white/90 text-sm leading-relaxed max-w-md">
                  {currentProgramData.description}
                </p>

              </div>
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className="px-4 pt-6 pb-2 bg-white/50 backdrop-blur-sm">
          <div className="space-y-3">
            {/* Progress Bar */}
            <div className="relative">
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className={`bg-gradient-to-r ${currentProgramData.gradient} h-3 rounded-full transition-all duration-700 shadow-sm`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="absolute -top-4 right-0 text-[10px] font-bold text-gray-700">
                {progress.toFixed(1)}%
              </span>
            </div>

            {/* Amount Details */}
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm text-gray-600">Terkumpul</div>
                <div className="font-bold text-md text-gray-900">
                  {formatCurrency(currentProgramData.collectedAmount)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">Target</div>
                <div className="font-bold text-md text-gray-900">
                  {formatCurrency(currentProgramData.targetAmount)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Program Selector Dots */}
      {programs.length > 1 && (
        <div className="flex justify-center items-center space-x-2">
          {programs.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentProgram(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentProgram
                  ? 'w-8 h-2 bg-orange-500'
                  : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SpecialPrograms;